import React from 'react';
import _ from 'lodash';
import {Form, Grid, Header, Icon} from 'semantic-ui-react';
import {produceAppliedDiff} from 'framework/collaboration/diff.utils';
import {
  calculateChildrenTotalByMode,
  calculateChildrenActualTotalByMode,
  calculateChildrenActualAllowedTotalByMode,
  toItemCode,
  filterConfig,
  getChildrenMapping,
  displayError,
  filterChangesForEditLabel,
  getEditsFromChildren,
  checkChildrenForEdits,
  getEditLabelFromEdits,
  requestDetailsChangesToDisplayString,
  createChangeObj,
  extractChangeParamsFromCfg,
} from 'common/requestDetails/utils';
import {
  InlineSelect,
  TextAreaField,
} from 'app-ui-componentlibrary/dist/go/components/common/FormControls';
import {ContentDispatch} from 'framework';
import {
  DEFAULT_CONFIG,
  MODES,
  FUNDING_STREAM_CODES,
  DESCRIPTION,
  CHANGES_TYPES,
  ADDITIONAL_INFORMATION,
} from 'common/requestDetails/constants';
import ItemDispatch from 'go/components/modules/requestDetails/v3/ItemDispatch';
import AddItemModal from 'go/components/modules/requestDetails/v3/AddItemModal';
import {DeleteModal} from 'app-ui-componentlibrary';
import {Contexts} from 'componentLibrary';
import MoreDetails from 'go/components/modules/requestDetails/v3/MoreDetails';
import ItemWrapper from 'go/components/modules/requestDetails/v3/items/shared/ItemWrapper';
import CustomGridLayout from './CustomGridLayout';
import {Alert} from 'componentLibrary';
import {Array, Object} from 'core-js';
import {produce} from 'immer';
import AddMilestone from 'go/components/dynamic/modules/AddMilestone';

const {RequestDetailsContext} = Contexts;
export class ContainerItemView extends React.Component {
  state = {
    deleteModalActive: false,
    addModalActive: false,
    tempItem: this.props.item,
    editMode: false,
  };
  pendingLineItem = null;
  lineItemUpdateComplete = false;

  /*
   * Check for changes on this item's level and (re)generate the "changes" array.
   */
  componentDidUpdate = (prevProps) => {
    const mode = _.get(this.context, 'mode');
    const showAdditionalQuestions = _.get(this.context, 'showAdditionalQuestions');
    const isReviewMode = mode === MODES.REVIEW;
    const prevItemDiffersFromCurrItem = !_.isEqual(
      _.get(prevProps, 'item.additionalInformation'),
      _.get(this.props, 'item.additionalInformation')
    );
    if (isReviewMode && showAdditionalQuestions && prevItemDiffersFromCurrItem)
      this.handleFieldChanges(prevProps);

    const prevPropsItem = prevProps.item;
    const currPropsItem = this.props.item;
    if (this.props.currentlyCollaborating) {
      if (prevPropsItem && currPropsItem && !_.isEqual(prevPropsItem, currPropsItem)) {
        this.handleChange(
          currPropsItem,
          prevPropsItem,
          this.props.currentlyCollaborating
        );
      } else if (
        this.pendingLineItem &&
        this.lineItemUpdateComplete &&
        !_.isEqual(this.state.tempItem, this.pendingLineItem)
      ) {
        this.lineItemUpdateComplete = false;
        // Force another state update
        this.setState({tempItem: this.pendingLineItem});
      } else if (this.lineItemUpdateComplete) {
        // Only clear pendingLineItem after componentDidUpdate runs
        // and we're certain the state is correct
        this.pendingLineItem = null;
      }
    }
  };

  handleFieldChanges = (prevProps) => {
    let {
      originalItem: oldItem,
      item: newItem,
      sourcePath,
      saveItem,
      itemConfig,
    } = this.props;
    const entityType = _.get(this.context, 'entityType');
    let reviewContents = _.get(itemConfig, 'additionalQuestions.reviewContents', []);
    if (entityType == 'amendment')
      reviewContents = _.get(itemConfig, 'additionalQuestions.amendmentContents', []);
    const changeParams = extractChangeParamsFromCfg(reviewContents);

    // changes coming from props, so need to get mutable copy
    let changes = _.get(newItem, 'additionalInformation.changes');
    changes = changes ? _.cloneDeep(changes) : [];
    let fieldThatCausedChange;

    const newlyCreated = _.find(
      _.get(newItem, 'additionalInformation.changes', []),
      (change) => change.type === 'newlyCreated'
    );

    _.forEach(changeParams, (changeParam) => {
      const {fieldName, fieldPath, displayText, changeType} = changeParam;
      const oldFieldVal = _.get(oldItem, fieldPath);
      const newFieldVal = _.get(newItem, fieldPath);
      const prevFieldVal = _.get(prevProps, 'item.' + fieldPath);
      if (oldItem) {
        if (!_.isEqual(oldFieldVal, newFieldVal)) {
          const changeObj = _.find(changes, (o) =>
            _.isEqual(_.get(o, 'field'), fieldName)
          );
          // Only add field changes if not a newly created item (award rec compared to application, amendment compared to grant)
          if (!newlyCreated) {
            if (changeObj) {
              _.set(changeObj, 'currValue', newFieldVal);
              _.set(changeObj, 'previousValue', oldFieldVal);
            } else {
              changes.push(
                createChangeObj(
                  changeType,
                  fieldName,
                  oldFieldVal,
                  newFieldVal,
                  displayText,
                  this.props.currentlyCollaborating
                )
              );
            }
          }
          if (!_.isEqual(newFieldVal, prevFieldVal)) fieldThatCausedChange = fieldName;
        } else if (!_.isEqual(newFieldVal, prevFieldVal)) {
          // changed field reverted back to old value, remove its change object
          fieldThatCausedChange = fieldName;
          _.remove(changes, (o) => _.isEqual(_.get(o, 'field'), fieldName));
        }
      } else {
        // we're iterating through fields of an added item, don't touch it's change object, save field that changed
        if (!_.isEqual(newFieldVal, prevFieldVal)) fieldThatCausedChange = fieldName;
      }
    });
    let updatedItem = produce(newItem, (draft) => {
      if (_.isEmpty(changes)) {
        _.unset(draft, ['additionalInformation', 'justification']);
      }
      _.set(draft, ['additionalInformation', 'changes'], changes);
    });

    if (fieldThatCausedChange) {
      saveItem(sourcePath, updatedItem, fieldThatCausedChange);
      this.handleChange(updatedItem, prevProps.item, this.props.currentlyCollaborating);
    }
  };

  static defaultProps = {
    itemConfig: {},
  };

  static contextType = RequestDetailsContext;

  toggleDeleteModal = () => {
    this.setState((prevState) => ({
      deleteModalActive: !prevState.deleteModalActive,
    }));
  };

  toggleAddModal = () => {
    this.setState((prevState) => ({
      addModalActive: !prevState.addModalActive,
    }));
  };

  toggleEditMode = () => this.setState({editMode: !this.state.editMode});

  handleChange = (tempItem, prevPropsItem, currentlyCollaborating) => {
    return new Promise((resolve) => {
      if (currentlyCollaborating) {
        if (!_.isEqual(prevPropsItem, tempItem)) {
          this.pendingLineItem = produceAppliedDiff(
            prevPropsItem,
            tempItem,
            this.state?.tempItem
          );
          this.lineItemUpdateComplete = false;
          this.setState({tempItem: this.pendingLineItem}, () => {
            this.lineItemUpdateComplete = true; // Mark as complete but DON'T clear pendingLineItem yet
            resolve();
          });
        } else {
          resolve();
        }
      } else {
        this.setState({tempItem}, () => {
          resolve();
        });
      }
    });
  };

  handleBlur = (fieldName) => {
    let {editable} = this.props;

    if (!editable) return;

    let {tempItem} = this.state;
    let {saveItem, sourcePath, item} = this.props;
    tempItem = _.cloneDeep(tempItem);
    let itemToDisplay = {
      ...tempItem,
      [ADDITIONAL_INFORMATION]: {
        ..._.get(item, ADDITIONAL_INFORMATION), // need to bring in additional information saved outside this component
        ..._.get(tempItem, ADDITIONAL_INFORMATION),
      },
    };

    saveItem(sourcePath, itemToDisplay, fieldName);
  };

  renderChangesSection = () => {
    const {editMode, tempItem} = this.state;
    const {
      validationErrors,
      validationDirtyMap,
      changeLabel,
      justificationLabel,
      editable,
    } = this.props;
    const justification = _.get(tempItem, 'additionalInformation.justification');
    const changes = _.get(tempItem, 'additionalInformation.changes');
    const formattedSortedChanges = _.map(changes, (change) =>
      requestDetailsChangesToDisplayString(change)
    );
    const JUSTIFICATION = 'additionalInformation.justification';
    const justificationLabelText = justificationLabel
      ? justificationLabel
      : 'Justification (included in award package)';
    const justificationErrors = displayError(
      'justification',
      JUSTIFICATION,
      validationErrors,
      validationDirtyMap,
      false
    );

    const newlyCreated = _.find(
      _.get(tempItem, 'additionalInformation.changes', []),
      (change) => change.type === 'newlyCreated'
    );

    return (
      <Grid className="diffingBackground change-section-grid">
        <Grid.Row>
          <Grid.Column computer={8} mobile={16}>
            <Header as="h5">{changeLabel ? changeLabel : 'Reviewer Edits'}</Header>
            <div>
              {_.map(formattedSortedChanges, (change, index) => {
                return <p key={index}>{change}</p>;
              })}
            </div>
          </Grid.Column>
          <Grid.Column computer={8} mobile={16}>
            {(editMode || newlyCreated) && editable ? (
              <TextAreaField
                style={{width: '100%'}}
                value={justification}
                title={justificationLabelText}
                label={justificationLabelText}
                id={`${JUSTIFICATION}-${this.props.id}`}
                name={JUSTIFICATION}
                handleChange={(e, data) => {
                  const newTempItem = produce(tempItem, (draft) => {
                    _.set(draft, JUSTIFICATION, data.value);
                  });
                  this.handleChange(
                    newTempItem,
                    tempItem,
                    this.props.currentlyCollaborating
                  );
                }}
                handleBlur={() => this.handleBlur(JUSTIFICATION)}
                displayError={justificationErrors.displayError}
                errorMessages={justificationErrors.errorMessages}
              />
            ) : (
              <div>
                <Header as="h5">{justificationLabelText}</Header>
                {justificationErrors.displayError ? (
                  <p className="errorMessages">{justificationErrors.errorMessages}</p>
                ) : (
                  justification
                )}
              </div>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  render() {
    let {
      item,
      editable,
      tierKey,
      originalItem,
      sourcePath,
      saveItem,
      addItem,
      itemConfig,
      beingCreated,
      itemCodeOptions,
      deleteItem,
      validationErrors,
      validationDirtyMap,
      moreDetails,
      addedActivityId,
      displayFields,
      disableFields,
      staffingStreamType,
    } = this.props;

    const projectName = _.get(item, 'additionalInformation.projectName', '');
    const mode = _.get(this.context, 'mode');
    const entityDataPath = _.get(this.context, 'entityDataPath');
    const showAdditionalQuestions = _.get(this.context, 'showAdditionalQuestions');
    const {deleteModalActive, addModalActive} = this.state;
    const {name, displayChildrenItemsInConfigOrder} = itemConfig;
    const children = _.get(item, 'children', {});
    const milestoneEnabled = _.get(itemConfig, 'enabledMilestone', false);
    const newlyCreated = _.find(
      _.get(item, 'additionalInformation.changes', []),
      (change) => change.type === 'newlyCreated'
    );
    const explainOtherItem = _.get(item, 'additionalInformation.explainOtherItem');
    const itemCode = _.get(item, 'itemCode');
    const deletable =
      mode === MODES.APPLICATION ||
      (!!newlyCreated &&
        mode !== MODES.CLOSEOUT_REPORT &&
        mode !== MODES.CLOSEOUT_REPORT_REVIEW) ||
      (mode === MODES.CLOSEOUT_REPORT && !originalItem);
    const isPaymentRequestHiring =
      mode === MODES.PAYMENT_REQUEST &&
      _.get(itemConfig, 'fundingStreamType') === staffingStreamType;
    const isCloseoutReview = mode === MODES.CLOSEOUT_REPORT_REVIEW;
    const childrenCosts = calculateChildrenTotalByMode(
      children,
      mode,
      isPaymentRequestHiring
    );
    const childrenActualCosts =
      mode === MODES.CLOSEOUT_REPORT || mode === MODES.CLOSEOUT_REPORT_REVIEW
        ? calculateChildrenActualTotalByMode(children, mode)
        : undefined;
    const childrenActualAllowedCosts =
      mode === MODES.CLOSEOUT_REPORT_REVIEW
        ? calculateChildrenActualAllowedTotalByMode(children, mode)
        : undefined;

    const tier = _.get(this.props, 'tier', 0);
    const itemLabel = _.get(itemConfig, 'labels.itemLabel', 'section');
    const itemDisplayTextLabel = _.get(
      itemConfig,
      'labels.itemDisplayText',
      `A ${itemLabel}`
    );
    const changes = _.get(item, 'additionalInformation.changes');
    let editLabel = '';
    if (changes && !_.isEmpty(changes)) {
      editLabel = getEditLabelFromEdits(changes);
    } else {
      editLabel = checkChildrenForEdits(children) ? 'Edited' : null;
    }
    const itemSuffix = _.get(itemConfig, 'labels.itemSuffix', '');
    const instructionText = _.get(itemConfig, 'instructionText', '');
    _.forEach(_.get(itemConfig, 'children'), (child) => {
      if (_.get(child, 'labels.itemLabel') == undefined) {
        _.set(child, 'labels.itemLabel', itemLabel);
      }
    });
    var itemLabelCounts = _.countBy(_.get(itemConfig, 'children'), 'labels.itemLabel');
    var childItemLabel = _.maxBy(_.keys(itemLabelCounts), function (i) {
      return itemLabelCounts[i];
    });
    /* Display tier depends on the program area */
    const milestoneDisplayTier = _.isEqual(childItemLabel, 'category') ? 1 : tier;
    var itemLabelDisplayTextCounts = _.countBy(
      _.get(itemConfig, 'children'),
      'labels.itemDisplayText'
    );
    var childItemDisplayTextLabel = _.maxBy(_.keys(itemLabelDisplayTextCounts), function (
      i
    ) {
      return itemLabelDisplayTextCounts[i];
    });
    /* data analysis for add item configuration */
    const childrenCodes = {};
    _.forEach(children, (child) =>
      _.set(childrenCodes, child.itemCode, _.get(childrenCodes, child.itemCode, 0) + 1)
    );
    // filters out children at their max occurance limit
    const filteredChildrenConfig = filterConfig(itemConfig.children, childrenCodes);
    const hasEligibleChildren = _.size(filteredChildrenConfig) > 0;
    const parentConfigWithOnlyEligible = {
      ...itemConfig,
      children: filteredChildrenConfig,
    };

    const childrenItemCodeOptions = getChildrenMapping(filteredChildrenConfig);
    const childrenItemCodeOptionsSorted = _.orderBy(
      childrenItemCodeOptions,
      [(item) => item.text],
      ['asc']
    );

    const missingChildrenReview = !_.get(
      validationErrors,
      `missingChildren.isValid`,
      true
    );
    const missingChildren =
      missingChildrenReview ||
      displayError('children', '', validationErrors, validationDirtyMap).displayError;

    const errorMessages = {
      missingChildren: `${_.capitalize(
        itemDisplayTextLabel
      )} must contain at least one ${childItemLabel}. You must add ${_.lowerCase(
        childItemDisplayTextLabel
      )} or remove this ${itemLabel}.`,
    };

    const headerTitle = `${itemLabel}: ${name}${projectName ? ' ' + projectName : ''}`;
    let isValid = true;
    let isNarrativeValid = true;
    _.forEach(_.get(validationErrors, 'error'), (err) => {
      isValid = isValid && err.isValid;
    });
    const getErrorsRecursivly = (validationErrors) => {
      const getchildren = _.get(validationErrors, 'children');
      if (getchildren) {
        getErrorsRecursivly(getchildren);
      } else {
        _.forEach(validationErrors, (err) => {
          const errors = _.get(err, 'error');
          if (errors) {
            _.forEach(errors, (error) => {
              isValid = isValid && _.get(error, 'isValid');
            });
          }
          if (_.get(err, 'children')) {
            getErrorsRecursivly(_.get(err, 'children'));
          }
        });
      }
    };
    getErrorsRecursivly(validationErrors);

    const getNarrativeErrors = (validationErrors) => {
      const errors = _.get(validationErrors, 'error');
      _.forEach(errors, (err) => {
        _.forIn(err, (errorVal, key) => {
          // filter out top-level children errors
          if (key !== 'isValid' && key !== 'children') {
            getNarrativeErrorsRecursively(errorVal);
          }
        });
      });
    };
    const getNarrativeErrorsRecursively = (error) => {
      if (error instanceof Array) {
        _.forEach(error, (err) => {
          getNarrativeErrorsRecursively(err);
        });
      } else if (_.get(error, 'isValid') !== undefined) {
        isNarrativeValid = isNarrativeValid && _.get(error, 'isValid');
      } else if (error instanceof Object) {
        _.forIn(error, (err, key) => {
          getNarrativeErrorsRecursively(err);
        });
      }
    };

    getNarrativeErrors(validationErrors);

    const getCloseoutReportReadOnlyContents = (editable, itemConfig) => {
      let contentsConfigPath = 'additionalQuestions.closeoutReportContentsReadOnly';
      if (
        editable &&
        _.has(itemConfig, 'additionalQuestions.closeoutReportContentsModification')
      ) {
        contentsConfigPath = 'additionalQuestions.closeoutReportContentsModification';
      }
      return contentsConfigPath;
    };

    const costTypeErrorList = _.filter(_.get(validationErrors, 'error', []), function (
      obj
    ) {
      return obj.path === 'costTypeError';
    });
    const costTypeErrorObj = _.find(
      costTypeErrorList,
      (costTypeError) => costTypeError.isValid === false
    );

    const showNarrative = !_.isEmpty(_.get(itemConfig, 'additionalQuestions', {}));
    const narrativeLabel = _.get(itemConfig, 'labels.narrativeLabel', 'questions');
    const narrativeActivityName = `${name} ${itemLabel} ${narrativeLabel}`;
    const hideFPSActivityQuestion =
      itemLabel === 'activity' && _.includes(disableFields, 'activityQuestion');

    const narative = () => {
      let narrativeContentPath;
      const editMode = this.state.editMode;
      const isReviewMode = mode === MODES.REVIEW || mode === MODES.CLOSEOUT_REPORT_REVIEW;
      const entityType = _.get(this.context, 'entityType');
      if (mode === MODES.APPLICATION) {
        narrativeContentPath = editable
          ? 'additionalQuestions.contents'
          : 'additionalQuestions.readOnlyContents';
      } else if (mode === MODES.CLOSEOUT_REPORT && showAdditionalQuestions) {
        narrativeContentPath =
          (editMode || newlyCreated) && editable && !originalItem
            ? 'additionalQuestions.closeoutReportContents'
            : getCloseoutReportReadOnlyContents(editable, itemConfig);
      } else if (isReviewMode && showAdditionalQuestions) {
        narrativeContentPath =
          (editMode || newlyCreated) && editable
            ? 'additionalQuestions.reviewContents'
            : 'additionalQuestions.reviewContentsReadOnly';
        if (entityType == 'amendment')
          narrativeContentPath =
            (editMode || newlyCreated) && editable
              ? 'additionalQuestions.amendmentContents'
              : 'additionalQuestions.reviewContentsReadOnly';
        if (mode === MODES.CLOSEOUT_REPORT_REVIEW)
          narrativeContentPath = 'additionalQuestions.reviewContentsReadOnly';
      }
      const narrativeContents = _.get(itemConfig, narrativeContentPath, {});
      const {item} = this.props;
      const changes = _.get(item, 'additionalInformation.changes');
      const hasChanges = changes && _.size(changes) > 0;
      const disableEditMode =
        mode === MODES.CLOSEOUT_REPORT_REVIEW ||
        _.get(itemConfig, 'disableEditMode', false);
      const headerOptions = isReviewMode && showNarrative && !disableEditMode;
      return (
        (mode === MODES.APPLICATION ||
          (mode === MODES.CLOSEOUT_REPORT && showAdditionalQuestions) ||
          (isReviewMode && showAdditionalQuestions)) &&
        !_.isEmpty(narrativeContents) &&
        showNarrative &&
        !hideFPSActivityQuestion && (
          <>
            <CustomGridLayout
              headerTitle={narrativeActivityName}
              tier={tier}
              mode={mode}
              name={itemConfig.name}
              itemLabel={itemLabel}
              deletable={deletable}
              handleRemove={this.toggleDeleteModal}
              isNarrative={true}
              sourcePath={sourcePath}
              expand={sourcePath === addedActivityId}
              editable={editable}
              isValid={isNarrativeValid}
              validationErrors={validationErrors}
              activityName={narrativeActivityName}
              renderChangesSection={this.renderChangesSection}
              hasChanges={
                hasChanges &&
                mode !== MODES.CLOSEOUT_REPORT &&
                mode !== MODES.CLOSEOUT_REPORT_REVIEW
              }
              toggleEditMode={this.toggleEditMode}
              newlyCreated={newlyCreated}
            >
              <Form>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <div className="margin-bottom-20">
                      <ul className="open-accordion-actions-ul w-clearfix w-list-unstyled reviewmode-ul">
                        {headerOptions && editable && !newlyCreated && (
                          <li className="open-accordion-actions-li reviewmode-li">
                            {!editMode ? (
                              <a href="#" onClick={this.toggleEditMode} tabIndex="0">
                                <Icon name="pencil" />
                                Edit this {itemLabel}
                              </a>
                            ) : (
                              <a href="#" onClick={this.toggleEditMode} tabIndex="0">
                                <Icon name="checkmark" />
                                Close edits
                              </a>
                            )}
                          </li>
                        )}
                      </ul>
                    </div>
                    {_.map(narrativeContents, (conf, index) => {
                      let configMap = {...conf};
                      if (!_.isUndefined(this.props.additionalQuestionsEntityType)) {
                        configMap.entityType = this.props.additionalQuestionsEntityType;
                      }
                      return (
                        <ContentDispatch
                          key={conf.id || index}
                          config={configMap}
                          rootPath={entityDataPath + '.' + sourcePath}
                        />
                      );
                    })}
                  </Grid.Column>
                </Grid.Row>
              </Form>
            </CustomGridLayout>
          </>
        )
      );
    };

    return (
      <React.Fragment>
        {beingCreated ? (
          <Form>
            <InlineSelect
              options={itemCodeOptions}
              label={
                itemSuffix === ''
                  ? 'Select ' + itemLabel + ':'
                  : 'Select ' + itemLabel + ' ' + itemSuffix + ':'
              }
              name={'itemCode'}
              value={itemConfig.itemCode}
              handleChange={(e) =>
                saveItem('', {
                  ...item,
                  itemCode: toItemCode(_.get(e, 'target.value')),
                })
              }
            />
            {this.props.children}
          </Form>
        ) : (
          <React.Fragment>
            <CustomGridLayout
              deleteItem={this.toggleDeleteModal}
              activityKey={tierKey}
              activityName={name}
              headerTitle={headerTitle}
              total={childrenCosts}
              actualTotal={childrenActualCosts}
              actualAllowedTotal={childrenActualAllowedCosts}
              tier={tier}
              mode={mode}
              name={itemConfig.name}
              itemLabel={itemLabel}
              deletable={deletable}
              handleRemove={this.toggleDeleteModal}
              isValid={isValid}
              validationErrors={validationErrors}
              sourcePath={sourcePath}
              instructionText={instructionText}
              expand={sourcePath === addedActivityId}
              customText={_.get(costTypeErrorObj, 'customText')}
              editable={editable}
              editLabel={editLabel}
            >
              {deletable ? (
                <DeleteModal
                  displayTitle={`Remove section`}
                  open={deleteModalActive}
                  cancelAction={this.toggleDeleteModal}
                  deleteAction={() => deleteItem(sourcePath)}
                  displayText={`Are you sure you want to remove this ${itemConfig.name} section? This will also remove the items within this section. This cannot be undone.`}
                />
              ) : (
                <React.Fragment />
              )}
              {hasEligibleChildren ? (
                <AddItemModal
                  active={addModalActive}
                  saveItem={(newKey, item) =>
                    addItem(sourcePath + '.children.' + newKey, item)
                  }
                  cancelAction={this.toggleAddModal}
                  parentConfigToMerge={parentConfigWithOnlyEligible}
                  eligibleChildren={parentConfigWithOnlyEligible.children}
                  addingToName={name}
                  existingCodes={childrenCodes}
                  itemCodeOptions={
                    displayChildrenItemsInConfigOrder
                      ? childrenItemCodeOptions
                      : childrenItemCodeOptionsSorted
                  }
                  displayFields={displayFields}
                />
              ) : (
                <React.Fragment />
              )}
              <div className={`request-details--v3__tier${tier}`}>
                <ItemWrapper
                  tier={tier}
                  itemLabel={itemLabel}
                  activityKey={tierKey}
                  activityName={name}
                  otherActivityName={itemCode == 56 && explainOtherItem}
                  allowAdd={
                    hasEligibleChildren && !isPaymentRequestHiring && !isCloseoutReview
                  }
                  deletable={deletable}
                  activityTotal={childrenCosts}
                  editable={editable}
                  deleteItem={this.toggleDeleteModal}
                  addItem={this.toggleAddModal}
                  sourcePath={sourcePath}
                  childItemLabel={childItemLabel}
                  narative={narative}
                  itemsExist={!_.isEmpty(childrenCodes)}
                  isNarrative={showNarrative}
                >
                  {this.props.children}
                  {!_.get(costTypeErrorObj, 'isValid', true) && (
                    <Alert
                      header={_.get(costTypeErrorObj, 'message')}
                      content=""
                      type="error"
                      dismissable={false}
                    />
                  )}

                  {missingChildren ? (
                    <p className="errorMessages">{errorMessages.missingChildren}</p>
                  ) : (
                    _.map(children, (childItem, childIndex) => {
                      let childConfig = _.find(
                        itemConfig.children,
                        (configChild) => childItem.itemCode === configChild.itemCode
                      );
                      let allChildrenAdditional = _.get(
                        itemConfig,
                        'additionalQuestions.allChildren',
                        {}
                      );
                      let childGroupAdditional = _.get(
                        _.find(
                          _.get(itemConfig, 'additionalQuestions.childrenGroups'),
                          (childGroup) =>
                            _.includes(childGroup.itemCodes, childItem.itemCode)
                        ),
                        'additionalQuestions',
                        {}
                      );
                      childConfig = {
                        ...itemConfig,
                        additionalQuestions: {
                          ...allChildrenAdditional,
                          ...childGroupAdditional,
                        },
                        ...DEFAULT_CONFIG,
                        ...childConfig,
                      };
                      let childProps = {
                        ...this.props,
                        item: childItem,
                        tierKey: childIndex,
                        tier: tier + 1,
                        itemConfig: childConfig,
                        sourcePath: `${sourcePath}.children`,
                        itemCodeOptions: childrenItemCodeOptions,
                        originalItem: _.get(originalItem, `children.${childIndex}`),
                        validationErrors: _.get(
                          validationErrors,
                          `children.${childIndex}`
                        ),
                        validationDirtyMap: _.get(
                          validationDirtyMap,
                          `children.${childIndex}`
                        ),
                        moreDetails: _.find(
                          _.get(moreDetails, 'children'),
                          (moreDetailSection) =>
                            moreDetailSection.itemCode === childItem.itemCode
                        ),
                      };
                      return <ItemDispatch {...childProps} key={childIndex} />;
                    })
                  )}
                </ItemWrapper>
                {!missingChildren &&
                  milestoneEnabled &&
                  _.isEqual(tier, milestoneDisplayTier) && (
                    <AddMilestone
                      {...this.props}
                      entityType={this.context.entityType}
                      entityDataPath={this.context.entityDataPath}
                    />
                  )}
              </div>
            </CustomGridLayout>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

export default ContainerItemView;
